import React, { useEffect, useState, useMemo } from 'react'
import { DialogContent, MenuItem, InputAdornment } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import StyledTextField from '../../Common/StyledTextField'
import { MainButton } from '../../Button/MainButton'
import { DIALOG_EDIT_MODES, TRAINING_LOCATION_TEMPLATE } from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import useConstants from '../../../hooks/useConstants'

const useStyles = makeStyles(() => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem',
		flex: 1
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div': {
			flex: 1
		},
		gap: '1.5rem'
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		},
		justifySelf: 'flex-end'
	},
	abbrAdornment: {
		marginLeft: '10px',
		marginRight: 0,
		marginTop: '19px !important',
		'& > p': {
			fontSize: '1rem'
		}
	}
}))

/**
 *
 * @param {{
  open: Boolean, 
	onClose: Function, 
	onCancelled: Function,
	title: String,
	mode: "new" | "edit", 
	location: TRAINING_LOCATION_TEMPLATE
}} params
 * @returns
 */
const TrainingLocationDialog = ({
	open,
	onSaved,
	onCancelled,
	title = 'Area',
	mode = DIALOG_EDIT_MODES.New,
	location = {}
}) => {
	const classes = useStyles()

	const [editedLocation, setEditedLocation] = useState(location)
	const [editedKeys, setEditedKeys] = useState(new Set())

	const { loading, facility, saveTrainingLocationForFacility, updateTrainingLocationForFacility } =
		useSelectedFacility()
	const { statesSkiptownOperatesInOrNear } = useConstants()

	useEffect(() => {
		// remove facility abbreviation from location abbreviation for editing, add it back on save
		location.abbreviation = location.abbreviation.replace(`${facility?.abbreviation}-`, '')
		setEditedLocation(location)
	}, [location, setEditedLocation])

	const handleResetDialog = () => {
		setEditedLocation({ ...TRAINING_LOCATION_TEMPLATE })
		setEditedKeys(new Set())
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleOnSave = async () => {
		const editedLocationWithFacilityAbbreviation = {
			...editedLocation,
			abbreviation: `${facility?.abbreviation}-${editedLocation.abbreviation}`
		}
		if (mode === DIALOG_EDIT_MODES.New) {
			await saveTrainingLocationForFacility({
				location: editedLocationWithFacilityAbbreviation,
				facility_id: facility._id
			})
		}
		if (mode === DIALOG_EDIT_MODES.Edit && editedKeys.size > 0) {
			const updates = {}

			for (const key of editedKeys) {
				updates[key] = editedLocation[key]
			}
			const { _id } = editedLocation
			await updateTrainingLocationForFacility({ location_id: _id, updates, facility_id: facility._id })
			setEditedKeys(new Set())
		}
		if (onSaved) {
			handleResetDialog()
			onSaved()
		}
	}

	const handleFieldUpdate = ({ field, targetValue }) => {
		const [mainField, subField] = field.split('.')
		setEditedLocation((prev) => {
			const revised = { ...prev }
			if (subField) {
				revised[mainField] = { ...revised[mainField], [subField]: targetValue }
			} else {
				revised[field] = targetValue
			}

			// Be strict with abbreviation entry, only allowing alphanumeric characters and hyphens
			if (field === 'abbreviation') {
				// remove any non-alphanumeric characters
				targetValue = targetValue.replace(/[^a-zA-Z0-9\-]/g, '')
				revised.abbreviation = targetValue.toUpperCase()
			}
			return revised
		})
		setEditedKeys((keys) => keys.add(mainField))
	}

	const canSave = useMemo(() => {
		return editedLocation.name > '' && editedLocation.abbreviation > ''
	}, [editedLocation])

	return (
		<>
			<StyledDialog open={open} onClose={onCancelled} title={title} showActivityIndicator={loading}>
				<DialogContent style={{ display: 'flex' }}>
					<div className={classes.dialogContent}>
						<div className={classes.rowWrapper}>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="Name"
										value={editedLocation.name}
										required={true}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										onChange={(e) =>
											handleFieldUpdate({ field: 'name', targetValue: e.target.value })
										}
									/>
								</div>
								<div>
									<StyledTextField
										variant="filled"
										label="Abbreviation (max 15 characters)"
										disabled={
											mode === DIALOG_EDIT_MODES.ViewOnly || mode === DIALOG_EDIT_MODES.Edit
										}
										value={editedLocation.abbreviation}
										inputProps={{ maxLength: 10 }}
										required={true}
										InputProps={{
											startAdornment: (
												<InputAdornment className={classes.abbrAdornment} position="start">
													{`${facility?.abbreviation}-`}
												</InputAdornment>
											)
										}}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'abbreviation',
												targetValue: e.target.value
											})
										}
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="Street Address"
										value={editedLocation.address.address_line_1}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'address.address_line_1',
												targetValue: e.target.value
											})
										}
									/>
								</div>
								<div>
									<StyledTextField
										variant="filled"
										label="City"
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										value={editedLocation.address.city}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'address.city',
												targetValue: e.target.value
											})
										}
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="State"
										select
										value={editedLocation.address.state}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'address.state',
												targetValue: e.target.value
											})
										}>
										{statesSkiptownOperatesInOrNear.map(({ name, abbreviation }) => {
											return (
												<MenuItem key={abbreviation} value={abbreviation}>
													{name}
												</MenuItem>
											)
										})}
									</StyledTextField>
								</div>
								<div>
									<StyledTextField
										variant="filled"
										label="Zip"
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										value={editedLocation.address.zip_code}
										inputProps={{ maxLength: 5 }}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'address.zip_code',
												targetValue: e.target.value
											})
										}
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="Internal Notes"
										multiline
										value={editedLocation.internal_notes}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										inputProps={{ maxLength: 1000, minRows: 3 }}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'internal_notes',
												targetValue: e.target.value
											})
										}
									/>
								</div>
							</div>
							<div className={classes.row}>
								<div>
									<StyledTextField
										variant="filled"
										label="External Notes"
										multiline
										value={editedLocation.external_notes}
										disabled={mode === DIALOG_EDIT_MODES.ViewOnly}
										inputProps={{ maxLength: 1000, minRows: 3 }}
										onChange={(e) =>
											handleFieldUpdate({
												field: 'external_notes',
												targetValue: e.target.value
											})
										}
									/>
								</div>
							</div>
						</div>
						<div className={classes.dialogActions}>
							{mode !== DIALOG_EDIT_MODES.ViewOnly ? (
								<MainButton
									label={mode === DIALOG_EDIT_MODES.New ? 'SAVE' : 'UPDATE'}
									onClick={handleOnSave}
									variant="contained"
									disabled={!canSave}
								/>
							) : null}

							<MainButton label="Cancel" onClick={handleOnCancelled} />
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
		</>
	)
}

export default TrainingLocationDialog
